import { GameMode } from '@/contexts/GameModeContext';
import { GameStatus, GuessHistory, KeyboardStatus, PreviousWord } from '@/lib/types/GameTypes';
import * as CookieUtils from '@/lib/cookieUtils';

/**
 * Atomic game state - all game data in a single cookie for true atomicity
 */
export interface AtomicGameState {
  // Core game state
  currentWord: string;
  currentLevel: number;
  gameStatus: GameStatus;
  guessHistory: GuessHistory;
  levelAttempts: number[];
  keyboardStatus: KeyboardStatus;
  previousWord: PreviousWord | null;
  currentGuessRow: number;
  isTransitioning: boolean;
  completedLevels: number[];
  
  // Metadata
  persistenceDate: string;
  lastPlayed: string;
  completionState: boolean;
  
  // Atomicity guarantees
  timestamp: string; // ISO timestamp of when state was written
  version: number; // Schema version for future migrations
  mode: GameMode; // Mode this state belongs to
}

const CURRENT_VERSION = 1;
const MAX_COOKIE_SIZE = 3800; // Safe limit below 4KB

/**
 * Atomic game storage - single cookie operations for true atomicity
 */
export class AtomicGameStorage {
  private static instance: AtomicGameStorage;
  private static migrationStatus: Map<string, boolean> = new Map();
  
  private constructor() {}
  
  static getInstance(): AtomicGameStorage {
    if (!AtomicGameStorage.instance) {
      AtomicGameStorage.instance = new AtomicGameStorage();
    }
    return AtomicGameStorage.instance;
  }

  /**
   * Get cookie name for a specific mode
   */
  private getCookieName(mode: GameMode): string {
    return `babblegram_${mode}_atomic_state`;
  }

  /**
   * Get cookie options
   */
  private getCookieOptions() {
    const isProduction = window.location.protocol === 'https:';
    const isLovablePreview = window.location.hostname.includes('lovable.dev');
    
    return {
      path: '/',
      secure: isProduction && !isLovablePreview,
      sameSite: (isProduction && !isLovablePreview) ? 'strict' as const : 'lax' as const,
      expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
    };
  }

  /**
   * Compress state if needed (simple minification)
   */
  private compressState(state: AtomicGameState): string {
    return JSON.stringify(state);
  }

  /**
   * Decompress state
   */
  private decompressState(compressed: string): AtomicGameState | null {
    try {
      return JSON.parse(compressed);
    } catch (error) {
      console.error('[AtomicStorage] Failed to decompress state:', error);
      return null;
    }
  }

  /**
   * Ensure legacy migration has run for this mode (idempotent, runs once per session)
   */
  private ensureMigrationComplete(mode: GameMode): void {
    const sessionKey = `babblegram_migration_complete_${mode}`;
    
    // Check session storage first
    try {
      if (sessionStorage.getItem(sessionKey) === 'true') {
        return; // Already migrated this session
      }
    } catch (error) {
      console.warn('[AtomicStorage] Session storage unavailable:', error);
    }

    // Check if we've already migrated in this instance
    if (AtomicGameStorage.migrationStatus.get(mode)) {
      return;
    }

    console.log(`[AtomicStorage] Running one-time migration check for ${mode}`);
    
    // Attempt migration
    const migrated = this.migrateFromLegacy(mode);
    
    // Mark as complete (even if nothing to migrate)
    AtomicGameStorage.migrationStatus.set(mode, true);
    try {
      sessionStorage.setItem(sessionKey, 'true');
    } catch (error) {
      console.warn('[AtomicStorage] Could not set migration flag in session storage:', error);
    }

    console.log(`[AtomicStorage] Migration check complete for ${mode}:`, { migrated });
  }

  /**
   * Read atomic game state for a specific mode
   */
  read(mode: GameMode): AtomicGameState | null {
    // Ensure migration is complete before reading
    this.ensureMigrationComplete(mode);

    const cookieName = this.getCookieName(mode);
    const value = CookieUtils.getCookie(cookieName);
    
    if (!value) {
      console.log(`[AtomicStorage] No state found for mode: ${mode}`);
      return null;
    }

    const state = this.decompressState(value);
    if (!state) {
      console.error(`[AtomicStorage] Invalid state for mode: ${mode}`);
      return null;
    }

    // Validate version
    if (state.version !== CURRENT_VERSION) {
      console.warn(`[AtomicStorage] Version mismatch. Expected ${CURRENT_VERSION}, got ${state.version}`);
      // Could trigger migration here in the future
    }

    // Validate mode
    if (state.mode !== mode) {
      console.error(`[AtomicStorage] Mode mismatch! Cookie for ${mode} contains data for ${state.mode}`);
      return null;
    }

    console.log(`[AtomicStorage] ✅ Read atomic state for ${mode}:`, {
      word: state.currentWord,
      level: state.currentLevel,
      status: state.gameStatus,
      date: state.persistenceDate,
      timestamp: state.timestamp,
      historyEntries: state.guessHistory.length
    });

    return state;
  }

  /**
   * Write atomic game state for a specific mode
   * This is the ONLY write operation - truly atomic!
   */
  write(mode: GameMode, state: Partial<AtomicGameState>): boolean {
    // Create full state with defaults
    const fullState: AtomicGameState = {
      currentWord: state.currentWord || '',
      currentLevel: state.currentLevel || 1,
      gameStatus: state.gameStatus || 'playing',
      guessHistory: state.guessHistory || [],
      levelAttempts: state.levelAttempts || [],
      keyboardStatus: state.keyboardStatus || {},
      previousWord: state.previousWord || null,
      currentGuessRow: state.currentGuessRow ?? 0,
      isTransitioning: state.isTransitioning ?? false,
      completedLevels: state.completedLevels || [],
      persistenceDate: state.persistenceDate || '',
      lastPlayed: state.lastPlayed || '',
      completionState: state.completionState || false,
      timestamp: new Date().toISOString(),
      version: CURRENT_VERSION,
      mode: mode
    };

    const compressed = this.compressState(fullState);
    const size = new Blob([compressed]).size;

    if (size > MAX_COOKIE_SIZE) {
      console.error(`[AtomicStorage] State too large: ${size} bytes (max ${MAX_COOKIE_SIZE})`);
      return false;
    }

    console.log(`[AtomicStorage] 💾 Writing atomic state for ${mode}:`, {
      word: fullState.currentWord,
      level: fullState.currentLevel,
      status: fullState.gameStatus,
      date: fullState.persistenceDate,
      size: `${size} bytes`,
      historyEntries: fullState.guessHistory.length
    });

    const cookieName = this.getCookieName(mode);
    CookieUtils.setCookie(cookieName, compressed, this.getCookieOptions());
    
    return true;
  }

  /**
   * Clear atomic state for a specific mode
   */
  clear(mode: GameMode): void {
    const cookieName = this.getCookieName(mode);
    console.log(`[AtomicStorage] 🗑️ Clearing atomic state for ${mode}`);
    CookieUtils.deleteCookie(cookieName);
  }

  /**
   * Check if atomic state exists for a mode
   */
  exists(mode: GameMode): boolean {
    const cookieName = this.getCookieName(mode);
    return CookieUtils.getCookie(cookieName) !== null;
  }

  /**
   * Migrate from legacy multi-cookie format to atomic single cookie
   * This reads old cookies and converts them to new format
   */
  migrateFromLegacy(mode: GameMode): boolean {
    console.log(`[AtomicStorage] 🔄 Attempting legacy migration for ${mode}`);
    
    // Check if already migrated (atomic cookie exists)
    if (this.exists(mode)) {
      console.log(`[AtomicStorage] Already migrated for ${mode}`);
      return false;
    }

    // Try to read legacy cookies
    const prefix = mode === 'daily' ? 'babblegram_daily_' : 
                   mode === 'practice' ? 'babblegram_practice_' : 
                   'babblegram_custom_';
    
    const get = (key: string) => CookieUtils.getCookie(prefix + key);
    const parse = <T>(s: string | null): T | null => {
      try {
        return s ? JSON.parse(s) : null;
      } catch {
        return null;
      }
    };

    const currentWord = get('current_word');
    const currentLevel = get('current_level');
    const gameStatus = get('game_status');
    const persistenceDate = get('game_persistence_date');

    // If no legacy data, nothing to migrate
    if (!currentWord || !currentLevel || !gameStatus || !persistenceDate) {
      console.log(`[AtomicStorage] No legacy data found for ${mode}`);
      return false;
    }

    // Build migrated state
    const migratedState: Partial<AtomicGameState> = {
      currentWord: currentWord,
      currentLevel: parseInt(currentLevel),
      gameStatus: gameStatus as GameStatus,
      guessHistory: parse<GuessHistory>(get('guess_history')) || [],
      levelAttempts: parse<number[]>(get('level_attempts')) || [],
      keyboardStatus: parse<KeyboardStatus>(get('keyboard_status')) || {},
      previousWord: parse<PreviousWord>(get('previous_word')) || null,
      currentGuessRow: 0, // Legacy didn't store this
      isTransitioning: false, // Legacy didn't store this
      completedLevels: [], // Legacy didn't store this
      persistenceDate: persistenceDate,
      lastPlayed: get('last_played') || persistenceDate,
      completionState: get('game_completion_state') === 'true'
    };

    console.log(`[AtomicStorage] ✅ Migrating legacy data for ${mode}:`, migratedState);
    
    // Write to new atomic format
    const success = this.write(mode, migratedState);
    
    if (success) {
      // Clean up old cookies
      const legacyKeys = [
        'game_status', 'current_word', 'current_level', 'guess_history',
        'level_attempts', 'keyboard_status', 'previous_word', 
        'game_persistence_date', 'last_played', 'game_completion_state'
      ];
      
      legacyKeys.forEach(key => {
        CookieUtils.deleteCookie(prefix + key);
      });
      
      console.log(`[AtomicStorage] ✅ Legacy migration completed for ${mode}`);
    }
    
    return success;
  }
}

export const atomicGameStorage = AtomicGameStorage.getInstance();
